import React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

/**
 * Reusable pagination bar.
 *   const { pageRows, controls } = usePagination(rows);
 *   return (<> <Table>{pageRows.map(...)}</Table> {controls} </>);
 */
export function usePagination(rows, initialSize = 25) {
  const [page, setPage] = React.useState(1);
  const [size, setSize] = React.useState(initialSize);
  const total = rows?.length || 0;
  const pageCount = Math.max(1, Math.ceil(total / size));
  const safePage = Math.min(page, pageCount);
  const start = (safePage - 1) * size;
  const pageRows = React.useMemo(() => (rows || []).slice(start, start + size), [rows, start, size]);

  // Reset to page 1 whenever the row count drops below current page
  React.useEffect(() => {
    if (page > pageCount) setPage(1);
  }, [page, pageCount]);

  const controls = (
    <Pagination
      page={safePage}
      pageCount={pageCount}
      size={size}
      total={total}
      onPage={setPage}
      onSize={(n) => { setSize(n); setPage(1); }}
    />
  );

  return { page: safePage, size, pageRows, pageCount, total, setPage, setSize, controls };
}


export function Pagination({ page, pageCount, size, total, onPage, onSize }) {
  if (!total) return null;
  const first = (page - 1) * size + 1;
  const last = Math.min(page * size, total);
  return (
    <div className="flex items-center justify-between gap-3 px-4 py-2 border-t border-zinc-200 bg-zinc-50/50 text-xs" data-testid="pagination-bar">
      <div className="text-zinc-500">
        Showing <b className="font-mono text-zinc-800">{first}-{last}</b> of <b className="font-mono text-zinc-800">{total}</b>
      </div>
      <div className="flex items-center gap-2">
        <span className="text-zinc-500 hidden md:inline">Rows per page</span>
        <Select value={String(size)} onValueChange={(v) => onSize(Number(v))}>
          <SelectTrigger className="h-7 w-[70px] text-xs" data-testid="page-size-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            {[10, 25, 50, 100].map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="flex items-center gap-1 ml-2">
          <button
            onClick={() => onPage(page - 1)}
            disabled={page <= 1}
            className="p-1 rounded border border-zinc-300 bg-white disabled:opacity-40 hover:bg-zinc-100"
            data-testid="page-prev"
          >
            <ChevronLeft size={14} />
          </button>
          <span className="font-mono px-2">{page} / {pageCount}</span>
          <button
            onClick={() => onPage(page + 1)}
            disabled={page >= pageCount}
            className="p-1 rounded border border-zinc-300 bg-white disabled:opacity-40 hover:bg-zinc-100"
            data-testid="page-next"
          >
            <ChevronRight size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}
